import { ref, computed } from 'vue'
import {
  createBroadcast as createBroadcastApi,
  fetchPendingBroadcasts,
  ackBroadcast as ackBroadcastApi,
  fetchBroadcastStatus as fetchBroadcastStatusApi,
} from '@/services/broadcastService'
import type { PendingBroadcast, CreateBroadcastInput, BroadcastStatusRow } from '@/types/broadcast'

const POLL_MS = 15_000 // mesmo intervalo de useForceLogout.ts/useAgentChat.ts

// Estado de módulo (singleton) — mesmo padrão de useAgentChat/useForceLogout:
// chamar useBroadcast() em vários componentes (App.vue, compositor, tela de
// histórico em Admin) reusa a mesma fila. startPolling/stopPolling ficam
// explícitos (não amarrados a onMounted/onUnmounted do composable) porque só
// App.vue deve controlar o timer — se fosse automático, o compositor abrindo/
// fechando pararia o polling global ao desmontar (mesmo risco resolvido em
// useAgentChat.ts, ver AgentChatDrawer.vue).
const pendingQueue = ref<PendingBroadcast[]>([])
const acking       = ref(false)
const sending      = ref(false)
const sendError    = ref('')

let pollTimer: ReturnType<typeof setInterval> | null = null
let pollUserId = 0

async function poll() {
  if (!pollUserId) return
  try {
    pendingQueue.value = await fetchPendingBroadcasts(pollUserId)
  } catch (err) {
    console.warn('[Broadcast] poll:', err)
  }
}

export function useBroadcast() {
  // Só o primeiro da fila é exibido por vez — evita empilhar vários modais se
  // o agente ficou offline enquanto mais de um aviso foi enviado; o próximo
  // aparece assim que o atual for confirmado.
  const currentBroadcast = computed<PendingBroadcast | null>(() => pendingQueue.value[0] ?? null)

  async function ack(broadcastId: number) {
    if (!pollUserId || acking.value) return
    acking.value = true
    try {
      await ackBroadcastApi(broadcastId, pollUserId)
      pendingQueue.value = pendingQueue.value.filter(b => b.id !== broadcastId)
    } catch (err) {
      console.warn('[Broadcast] ack:', err)
    } finally {
      acking.value = false
    }
  }

  async function sendBroadcast(input: CreateBroadcastInput) {
    sendError.value = ''
    sending.value = true
    try {
      await createBroadcastApi(input)
    } catch (err) {
      console.warn('[Broadcast] sendBroadcast:', err)
      sendError.value = 'Não foi possível enviar o aviso.'
      throw err
    } finally {
      sending.value = false
    }
  }

  // senderId omitido = todos os avisos (tela de histórico decide se filtra
  // só "meus avisos" ou mostra de todo mundo).
  async function fetchStatus(senderId?: number): Promise<BroadcastStatusRow[]> {
    return fetchBroadcastStatusApi(senderId)
  }

  /** Chamado uma única vez no setup() de App.vue — mesmo padrão de useForceLogout(). */
  function startPolling(userId: number) {
    pollUserId = userId
    if (pollTimer) return // já rodando — evita timer duplicado em remount
    pollTimer = setInterval(poll, POLL_MS)
  }

  // Fase 4.2 do plano de otimização de login (docs/planos/PLANO-OTIMIZACAO-LOGIN.md):
  // limpa a fila também, não só o timer — startPolling() do próximo agente só
  // faz o primeiro poll depois de POLL_MS, então sem isso um agente B logando
  // na mesma aba veria o aviso em massa pendente de A por até 15s.
  function stopPolling() {
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null }
    pollUserId = 0
    pendingQueue.value = []
  }

  return {
    pendingQueue,
    currentBroadcast,
    acking,
    sending,
    sendError,
    ack,
    sendBroadcast,
    fetchStatus,
    startPolling,
    stopPolling,
  }
}
